import React, { useState, useEffect, useContext, useRef } from 'react';
import Logger from '../../shared/Logger';
import {
  Box,
  Flex,
  Flow,
  Text,
  Button,
  Select,
  Input,
  theme,
  FormMessage,
  Checkbox,
  Label,
} from '@xometry/xometry_loft';
import usersApi from '../../services/users';
import { shippingServiceOptions } from '../../utils/constants';
import AuthContext from '@xometry/auth-context';
import { useMutation } from '@apollo/client';
import {
  CreateOrganizationShippingAccountMutationResult,
  CreateOrganizationShippingAccountMutationVariables,
  CreateOrganizationShippingAccountQuery,
  DeleteOrganizationShippingAccountByIdMutationResult,
  DeleteOrganizationShippingAccountByIdMutationVariables,
  DeleteOrganizationShippingAccountByIdQuery,
  GetOrganizationShippingAccountsByOrganizationIdQuery,
  UpdateOrganizationShippingAccountByIdQuery,
} from '../../schema/generated/schema';
import { ShippingAccountsTableProps } from './ShippingAccountTable';
import { entityTypeEnum } from '../ShippingAddress/types';
import {
  ShippingProvider,
  toShippingProvider,
  validateShippingAccount,
  type ShippingAccountValidation,
} from './shippingProvider';

function validateAccountForService(
  trimmedAccount: string,
  serviceValue: string
): ShippingAccountValidation {
  const provider = toShippingProvider(serviceValue);
  if (!provider) {
    return { valid: true, message: null };
  }
  return validateShippingAccount(trimmedAccount, provider);
}

const EditShippingAccountForm = ({
  entityID,
  entityType,
  exitEditMode,
  refreshAuth,
  shippingAccountsLength,
  accountToEdit,
  isEditMode,
  onOnboardingActionCompleted,
}: {
  entityID: string;
  entityType: entityTypeEnum;
  exitEditMode: () => void;
  refreshAuth: () => void;
  shippingAccountsLength: number;
  accountToEdit: ShippingAccountsTableProps | null;
  isEditMode: boolean;
  onOnboardingActionCompleted: () => void;
}) => {
  const [accountNumber, setAccountNumber] = useState('');
  const [shippingService, setShippingService] = useState('');
  const [isTeamDefault, setIsTeamDefault] = useState(
    shippingAccountsLength === 0 ? true : false
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSelectValid, setIsSelectValid] = useState(true);
  const [showAccountFieldErrors, setShowAccountFieldErrors] = useState(false);
  const [accountValidation, setAccountValidation] =
    useState<ShippingAccountValidation>({ valid: true, message: null });

  const shippingServiceRef = useRef('');
  const accountNumberRef = useRef('');
  const { user, isStaff } = useContext(AuthContext);

  const selectedProvider = toShippingProvider(shippingService);
  const accountInputMode =
    selectedProvider === ShippingProvider.Fedex ||
    selectedProvider === ShippingProvider.Dhl
      ? 'numeric'
      : 'text';

  const syncAccountValidationFromInputs = (
    rawAccount: string,
    serviceValue: string
  ) => {
    const trimmed = rawAccount.trim();
    setAccountValidation(validateAccountForService(trimmed, serviceValue));
  };

  const [createOrganizationShippingAccount] = useMutation<
    CreateOrganizationShippingAccountMutationResult,
    CreateOrganizationShippingAccountMutationVariables
  >(CreateOrganizationShippingAccountQuery, {
    refetchQueries: [
      GetOrganizationShippingAccountsByOrganizationIdQuery,
      'GetOrganizationShippingAccountsByOrganizationId',
    ],
  });

  const [updateOrganizationShippingAccountById] = useMutation(
    UpdateOrganizationShippingAccountByIdQuery,
    {
      refetchQueries: [
        GetOrganizationShippingAccountsByOrganizationIdQuery,
        'GetOrganizationShippingAccountsByOrganizationId',
      ],
    }
  );

  const handleShippingServiceChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = e.target.value;
    shippingServiceRef.current = value;

    if (value.length === 0) {
      setIsSelectValid(false);
      setShippingService('');
      shippingServiceRef.current = '';
      return;
    }

    setIsSelectValid(true);
    setShippingService(value);

    if (showAccountFieldErrors) {
      syncAccountValidationFromInputs(accountNumberRef.current, value);
    }
  };

  const handleAccountNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    accountNumberRef.current = value;
    setAccountNumber(value);
    if (showAccountFieldErrors) {
      syncAccountValidationFromInputs(value, shippingServiceRef.current);
    }
  };

  const handleAccountNumberBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    accountNumberRef.current = value;
    setShowAccountFieldErrors(true);
    syncAccountValidationFromInputs(value, shippingServiceRef.current);
  };

  useEffect(() => {
    if (isEditMode && accountToEdit) {
      const nextAccount = accountToEdit.account_number;
      const nextProvider = accountToEdit.provider;
      setAccountNumber(nextAccount);
      accountNumberRef.current = nextAccount;
      setShippingService(nextProvider);
      shippingServiceRef.current = nextProvider;
      setIsTeamDefault(!!accountToEdit.isDefault);
      setShowAccountFieldErrors(false);
      setAccountValidation({ valid: true, message: null });
    }
  }, [accountToEdit, isEditMode]);

  const addNewShippingAccount = () => {
    const serviceValue = shippingServiceRef.current;
    if (serviceValue.length === 0) {
      setIsSelectValid(false);
      return;
    }

    const trimmedAccountNumber = accountNumberRef.current.trim();
    setShowAccountFieldErrors(true);

    const accountResult = validateAccountForService(
      trimmedAccountNumber,
      serviceValue
    );
    setAccountValidation(accountResult);

    if (!accountResult.valid) {
      return;
    }

    const payload = {
      shipping_account: {
        provider: serviceValue,
        account_number: trimmedAccountNumber,
      },
    };
    setIsSubmitting(true);

    if (entityType === entityTypeEnum.user) {
      usersApi
        .postAddShippingAccount(entityID, payload)
        .then(() => {
          if (window.analytics && user) {
            window.analytics.track('New Customer Shipping Account Added', {
              instance: 'user_settings',
              shipping_service: payload.shipping_account.provider,
              shipping_account_number: payload.shipping_account.account_number,
              isStaff,
              userId: user.id,
              email: user.emailAddress,
            });
          }
          refreshAuth();
        })
        .catch((e) => {
          console.error(e);
        });
    } else if (entityType === entityTypeEnum.team) {
      if (isEditMode) {
        updateOrganizationShippingAccountById({
          variables: {
            id: accountToEdit?.id,
            organizationId: entityID,
            shippingAccount: {
              provider: serviceValue,
              accountNumber: trimmedAccountNumber,
              isTeamDefault: isTeamDefault,
              organizationId: entityID,
            },
          },
        })
          .then(() => {
            exitEditMode();
            refreshAuth();
          })
          .catch((error) => {
            Logger.error('Error updating shipping account', error, {
              component: 'EditShippingAccountForm',
              action: 'updateShippingAccount',
            });
          });
      } else {
        createOrganizationShippingAccount({
          variables: {
            shippingAccount: {
              provider: serviceValue,
              accountNumber: trimmedAccountNumber,
              isTeamDefault: isTeamDefault,
              organizationId: entityID,
            },
          },
        })
          .then(() => {
            exitEditMode();
            refreshAuth();
            onOnboardingActionCompleted();
          })
          .catch((error) => {
            Logger.error('Error creating shipping account', error, {
              component: 'EditShippingAccountForm',
              action: 'createShippingAccount',
            });
          });
      }
    }
  };

  const [deleteOrganizationShippingAccountById] = useMutation<
    DeleteOrganizationShippingAccountByIdMutationResult,
    DeleteOrganizationShippingAccountByIdMutationVariables
  >(DeleteOrganizationShippingAccountByIdQuery, {
    refetchQueries: [
      GetOrganizationShippingAccountsByOrganizationIdQuery,
      'GetOrganizationShippingAccountsByOrganizationId',
    ],
  });

  const removeShippingAccount = (id?: string) => {
    deleteOrganizationShippingAccountById({
      variables: {
        id: id || '',
        organizationId: entityID,
      },
    });
    exitEditMode();
  };

  const accountFieldInvalid =
    showAccountFieldErrors && !accountValidation.valid;
  const accountErrorMessage =
    showAccountFieldErrors && !accountValidation.valid
      ? accountValidation.message
      : null;

  // USPS remains valid for existing saved accounts but should not be offered
  // when adding a new own shipping account.
  const selectableServiceOptions = shippingServiceOptions.filter(
    (option) => option.value !== 'usps' || shippingService === 'usps'
  );

  return (
    <Flex
      flexDirection="column"
      gap={theme.space[3]}
      marginTop={theme.space[3]}
      width="50%"
    >
      <Text kind="body-title">
        {isEditMode ? 'Edit' : 'Add'} Shipping Account
      </Text>
      <Text kind="body-sm">
        By providing your account number you are representing that it is a valid
        shipping account number, and that you or your company are responsible
        for all shipping charges.
      </Text>
      <Flex flexDirection="column">
        {entityType === entityTypeEnum.team && (
          <Flex marginBottom={theme.space[2]} alignItems="center">
            <Checkbox
              id="defaultIndicator"
              checked={isTeamDefault}
              onChange={(e) => setIsTeamDefault(e.target.checked)}
              disabled={shippingAccountsLength === 0}
            />
            <Label
              paddingLeft={theme.space[1]}
              htmlFor="defaultIndicator"
              scale="large"
              lineHeight={theme.lineHeights[3]}
              fontWeight={theme.fontWeights.regular}
              marginBottom={theme.space[0]}
            >
              Make this my default shipping account
            </Label>
          </Flex>
        )}
        <Select
          width="100%"
          label="Shipping Service"
          placeholder="Select a service..."
          onChange={handleShippingServiceChange}
          invalid={!isSelectValid}
          value={shippingService}
        >
          {!shippingService && <option value="">Please Select...</option>}
          {selectableServiceOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        {!isSelectValid && (
          <FormMessage invalid>Please select a shipping service.</FormMessage>
        )}
      </Flex>
      <Box>
        <Input
          width="100%"
          label="Account Number"
          value={accountNumber}
          inputMode={accountInputMode}
          autoComplete="off"
          onChange={handleAccountNumberChange}
          onBlur={handleAccountNumberBlur}
          invalid={accountFieldInvalid}
        />
        {accountErrorMessage && (
          <FormMessage invalid>{accountErrorMessage}</FormMessage>
        )}
      </Box>
      <Flex justifyContent="space-between">
        {isEditMode && (
          <Button
            kind="text-red"
            type="button"
            onClick={() => removeShippingAccount(accountToEdit?.id)}
          >
            Remove
          </Button>
        )}
        <Flex gap={theme.space[2]} justifyContent="flex-end" marginLeft="auto">
          <Button onClick={() => exitEditMode()} kind={theme.kind.neutral}>
            Cancel
          </Button>
          <Button
            kind={isSubmitting ? 'outline-blue' : theme.kind.primary}
            onClick={addNewShippingAccount}
          >
            {isSubmitting ? <Flow /> : 'Save'}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default EditShippingAccountForm;
